import React from 'react';
import { Link } from 'react-router-dom';
import { Download, ArrowRight } from 'lucide-react';
import {
  ACOUSTIC_CAMERAS,
  COMPARE_ROW_GROUPS,
  getCellValue,
} from '../data/acousticCameras';

/**
 * Apple.com-style comparison table for the FOTRIC acoustic camera line.
 *
 * Layout:
 *   • Sticky product header row: image + name + tagline + Learn more link + Datasheet button
 *   • Grouped spec rows with subtle section headings on the left rail
 *   • Alternating row background for scanability
 *   • Horizontal scroll on narrow viewports (5 columns is wide)
 */
const AcousticCamerasCompareTable = () => {
  const cameras = ACOUSTIC_CAMERAS;

  return (
    <section
      className="py-16 sm:py-20 bg-white"
      data-testid="acoustic-compare-table-section"
      id="compare"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-10 text-center mx-auto">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1e2d3b] mb-3">
            Compare Every Acoustic Camera
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-orange-500 mx-auto mb-6"></div>
          <p className="text-lg text-slate-600 leading-relaxed">
            Every FOTRIC acoustic camera in the ctrl.aero line side-by-side. Pick the model that fits
            your range, workflow, and budget — then jump straight to the full product page.
          </p>
        </div>

        {/* Table wrapper is horizontally scrollable so 5 columns work on any viewport */}
        <div
          className="overflow-x-auto rounded-2xl border border-slate-200 shadow-sm"
          data-testid="compare-table-scroll"
        >
          <table className="w-full min-w-[900px] border-collapse">
            {/* Product header row */}
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-white w-56 min-w-[14rem] text-left align-bottom p-6 border-b border-slate-200">
                  <span className="text-xs uppercase tracking-widest text-slate-400 font-bold">
                    Model
                  </span>
                </th>
                {cameras.map((cam) => (
                  <th
                    key={cam.id}
                    className="align-top p-6 border-b border-l border-slate-200 bg-white min-w-[180px]"
                    data-testid={`compare-header-${cam.id}`}
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className="h-28 w-full flex items-center justify-center mb-3">
                        {cam.image ? (
                          <img
                            src={cam.image}
                            alt={`FOTRIC ${cam.name} acoustic imaging camera`}
                            className="max-h-28 w-auto object-contain"
                            loading="lazy"
                          />
                        ) : (
                          <div className="h-28 w-full bg-slate-50 rounded-lg" />
                        )}
                      </div>
                      <p className="text-lg font-bold text-[#1e2d3b] leading-tight mb-1">
                        FOTRIC {cam.name}
                      </p>
                      <p className="text-xs text-blue-600 font-medium mb-4 leading-snug">
                        {cam.tagline}
                      </p>
                      <div className="flex flex-col gap-2 w-full items-stretch">
                        <Link
                          to={cam.productPage}
                          className="inline-flex items-center justify-center gap-1 text-sm font-semibold text-blue-600 hover:text-orange-500 transition-colors"
                          data-testid={`compare-learn-more-${cam.id}`}
                        >
                          Learn more
                          <ArrowRight className="w-3.5 h-3.5" />
                        </Link>
                        {cam.datasheet ? (
                          <a
                            href={cam.datasheet}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-700 transition-colors"
                            data-testid={`compare-datasheet-${cam.id}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                            Data sheet
                          </a>
                        ) : (
                          <Link
                            to="/#contact"
                            className="inline-flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-full border border-slate-300 text-slate-700 hover:border-blue-500 hover:text-blue-600 transition-colors"
                            data-testid={`compare-request-datasheet-${cam.id}`}
                          >
                            <Download className="w-3.5 h-3.5" />
                            Request data sheet
                          </Link>
                        )}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {COMPARE_ROW_GROUPS.map((group, gi) => (
                <React.Fragment key={group.label}>
                  {/* Group heading row */}
                  <tr>
                    <td
                      colSpan={cameras.length + 1}
                      className="bg-slate-50 px-6 py-3 text-[11px] uppercase tracking-widest font-bold text-slate-500 border-t border-slate-200"
                    >
                      {group.label}
                    </td>
                  </tr>
                  {group.rows.map((row, ri) => (
                    <tr
                      key={`${gi}-${row.key}`}
                      id={`row-${row.key}`}
                      data-testid={`compare-row-${row.key}`}
                      className={`compare-row scroll-mt-32 ${ri % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
                    >
                      <th
                        scope="row"
                        className="sticky left-0 z-10 bg-inherit text-left align-top px-6 py-4 font-medium text-slate-700 text-sm border-b border-slate-100 min-w-[14rem]"
                      >
                        {row.label}
                      </th>
                      {cameras.map((cam) => {
                        const val = getCellValue(cam, row) || '—';
                        const isDash = val === '—';
                        return (
                          <td
                            key={cam.id}
                            className="align-top px-4 py-4 text-sm text-slate-700 border-b border-l border-slate-100 leading-snug"
                            data-testid={`compare-cell-${cam.id}-${row.key}`}
                          >
                            <span className={isDash ? 'text-slate-300' : ''}>{val}</span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <p className="text-center text-xs text-slate-500 mt-4">
          Specifications from FOTRIC product datasheets. Contact ctrl.aero for current pricing,
          availability, and calibration options.
        </p>
      </div>
    </section>
  );
};

export default AcousticCamerasCompareTable;
